import React from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";

const CREATED_AT_FILTER_KEYS = new Set(["created_at_from", "created_at_to"]);

export const REPORTS_WITHOUT_CREATED_AT_FILTERS = new Set([
  "client",
  "questionnaire",
  "users_roles",
  "activities",
]);

const filterStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const filterFieldSx = {
  width: "100%",
};

const emptySelectValue = (text) => (
  <Box component="span" sx={{ color: "text.secondary" }}>
    {text}
  </Box>
);

const buildSelectProps = (placeholder, resolveLabel) => ({
  displayEmpty: true,
  renderValue: (value) => {
    if (value === "" || value == null) return emptySelectValue(placeholder);
    return resolveLabel ? resolveLabel(value) : value;
  },
});

const resolveOptionLabel = (options, value) => {
  const match = (options || []).find((item) => item.value === value);
  return match?.label || value;
};

const SUBJECT_TERMINOLOGY_OVERRIDES = {
  patient: {
    label: "Subject",
    description: "Subjects with enrollment and survey completion",
  },
};

const replacePatientTerminology = (text) => {
  if (typeof text !== "string" || !text) return text;
  return text
    .replace(/\bPatients\b/g, "Subjects")
    .replace(/\bPatient\b/g, "Subject")
    .replace(/\bpatients\b/g, "subjects")
    .replace(/\bpatient\b/g, "subject");
};

const sanitizeReportConfig = (reports = []) =>
  reports.map((report) => {
    const typeOverride = SUBJECT_TERMINOLOGY_OVERRIDES[report.report_type] || {};
    return {
    report_type: report.report_type,
    label: typeOverride.label || replacePatientTerminology(report.label),
    description: typeOverride.description || replacePatientTerminology(report.description),
    default_sort_by: report.default_sort_by,
    default_sort_order: report.default_sort_order,
    admin_only: report.admin_only,
    admin_manager_only: report.admin_manager_only,
    filters: (report.filters || [])
      .filter((field) => {
        if (REPORTS_WITHOUT_CREATED_AT_FILTERS.has(report.report_type)) {
          return !CREATED_AT_FILTER_KEYS.has(field.key);
        }
        return true;
      })
      .map((field) => ({
      key: field.key,
      label: replacePatientTerminology(field.label),
      type: field.type,
      placeholder: replacePatientTerminology(field.placeholder),
      options: (field.options || []).map((option) => ({
        ...option,
        label: replacePatientTerminology(option.label),
      })),
      depends_on: field.depends_on,
    })),
    sortable_fields: (report.sortable_fields || []).map((field) => ({
      key: field.key,
      label: replacePatientTerminology(field.label),
    })),
  };
  });

const ReportAdvancedFilters = ({
  reportConfig,
  filters,
  onFilterChange,
  clients = [],
  projects = [],
  sortOrderOptions = [],
}) => {
  if (!reportConfig) return null;

  const visibleFilters = (reportConfig.filters || []).filter((field) => {
    if (REPORTS_WITHOUT_CREATED_AT_FILTERS.has(reportConfig.report_type)) {
      return !CREATED_AT_FILTER_KEYS.has(field.key);
    }
    return true;
  });

  const renderFilterField = (field) => {
    const value = filters[field.key] ?? "";
    const commonProps = {
      key: field.key,
      fullWidth: true,
      size: "small",
      label: field.label,
      value,
      onChange: (event) => onFilterChange(field.key, event.target.value),
      sx: filterFieldSx,
      InputLabelProps: { shrink: true },
      placeholder: field.placeholder || "",
    };

    if (field.type === "date") {
      return <TextField {...commonProps} type="date" />;
    }

    if (field.type === "text") {
      return <TextField {...commonProps} />;
    }

    if (field.type === "client_select") {
      return (
        <TextField
          {...commonProps}
          select
          SelectProps={buildSelectProps(field.placeholder || "All clients", (selected) => {
            const client = clients.find((item) => item.cid === selected);
            return client?.name || selected;
          })}
        >
          <MenuItem value="">{field.placeholder || "All clients"}</MenuItem>
          {clients.map((client) => (
            <MenuItem key={client.cid} value={client.cid}>
              {client.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.type === "project_select") {
      const dependsOn = field.depends_on || "client_id";
      const parentValue = filters[dependsOn] || filters.cid || "";
      return (
        <TextField
          {...commonProps}
          select
          disabled={!parentValue}
          SelectProps={buildSelectProps(field.placeholder || "All projects", (selected) => {
            const project = projects.find((item) => String(item.id) === String(selected));
            return project?.name || selected;
          })}
        >
          <MenuItem value="">{field.placeholder || "All projects"}</MenuItem>
          {projects.map((project) => (
            <MenuItem key={project.id} value={String(project.id)}>
              {project.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.type === "select") {
      return (
        <TextField
          {...commonProps}
          select
          SelectProps={buildSelectProps(
            field.placeholder || "All",
            (selected) => resolveOptionLabel(field.options, selected)
          )}
        >
          <MenuItem value="">{field.placeholder || "All"}</MenuItem>
          {(field.options || []).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return null;
  };

  return (
    <Box>
      <Box sx={filterStackSx}>{visibleFilters.map((field) => renderFilterField(field))}</Box>

      <Box sx={{ ...filterStackSx, mt: 2, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
          Sort
        </Typography>
        <TextField
          fullWidth
          size="small"
          select
          label="Sort by"
          value={filters.sort_by || reportConfig.default_sort_by}
          onChange={(event) => onFilterChange("sort_by", event.target.value)}
          sx={filterFieldSx}
          InputLabelProps={{ shrink: true }}
        >
          {(reportConfig.sortable_fields || []).map((field) => (
            <MenuItem key={field.key} value={field.key}>
              {field.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          size="small"
          select
          label="Sort order"
          value={filters.sort_order || reportConfig.default_sort_order}
          onChange={(event) => onFilterChange("sort_order", event.target.value)}
          sx={filterFieldSx}
          InputLabelProps={{ shrink: true }}
        >
          {(sortOrderOptions.length
            ? sortOrderOptions
            : [
                { value: "asc", label: "Ascending" },
                { value: "desc", label: "Descending" },
              ]
          ).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
  );
};

export { sanitizeReportConfig };
export default ReportAdvancedFilters;
